import { Component, HostListener, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { DataService } from '../../../shared/services/data.service';

interface AllExecutionReportRow {
  executionDate: string;
  projectType: string;
  brand: string;
  region: string;
  platform: string;
  buildName: string;
  executionName: string;
  testingType: string;
  passCount: number;
  failCount: number;
  skipCount: number;
  jobStatus: number;
  statusText: string;
  createdBy: string;
}

@Component({
  selector: 'wtaf-all-execution-report-widget',
  templateUrl: './all-execution-report-widget.component.html',
  styleUrls: ['./all-execution-report-widget.component.css']
})
export class AllExecutionReportWidgetComponent implements OnInit {
  projectTypeList: any[] = [];
  regionList: any[] = [];
  brandList: any[] = [];
  platformList: any[] = [];
  reportRows: AllExecutionReportRow[] = [];
  allReportRows: AllExecutionReportRow[] = [];
  userOptions: string[] = [];
  selectedUsers: string[] = [];
  showUserDropdown = false;

  selectedProjectTypeId = 0;
  selectedRegionId = 0;
  selectedBrandId = 0;
  selectedPlatformId = 0;
  selectedTestingType = 'All';
  buildName = '';
  fromDate = '';
  toDate = '';

  loading = false;
  downloadLoading = false;
  errorMessage = '';
  showAll = false;
  readonly displayCount = 6;

  testingTypes: string[] = [
    'All',
    'Smoke',
    'CICD',
    'Regression',
    'Compatibility',
    'Localization',
    'Trial-Run'
  ];

  constructor(private dataservice: DataService, private datePipe: DatePipe) {}

  ngOnInit() {
    const today = new Date();
    this.toDate = this.datePipe.transform(today, 'yyyy-MM-dd') || '';
    this.fromDate = moment(today)
      .subtract(7, 'days')
      .format('YYYY-MM-DD');

    this.loadProjectTypes();
  }

  get visibleRows(): AllExecutionReportRow[] {
    return this.showAll
      ? this.reportRows
      : this.reportRows.slice(0, this.displayCount);
  }

  get canSeeMore(): boolean {
    return this.reportRows.length > this.displayCount;
  }

  get totalCount(): number {
    return this.reportRows.length;
  }

  get runningCount(): number {
    return this.getStatusCount(1);
  }

  get completedCount(): number {
    return this.getStatusCount(2);
  }

  get incompleteCount(): number {
    return this.getStatusCount(3);
  }

  get terminatedCount(): number {
    return this.getStatusCount(4);
  }

  get totalSkippedCount(): number {
    return this.reportRows.reduce((sum, row) => sum + row.skipCount, 0);
  }

  toggleSeeMore() {
    this.showAll = !this.showAll;
  }

  onProjectTypeChange() {
    this.selectedRegionId = 0;
    this.selectedBrandId = 0;
    this.selectedPlatformId = 0;
    this.regionList = [];
    this.brandList = [];
    this.platformList = [];
    this.loadRegionsByProjectType();
    this.loadPlatformsByProjectType();
  }

  onRegionChange() {
    this.selectedBrandId = 0;
    this.brandList = [];
    this.loadBrandsByRegion();
  }

  fetchReport() {
    if (!this.isFilterValid()) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.dataservice
      .getExecutionReport(
        this.getSelectedBrandName(),
        this.fromDate,
        this.toDate,
        this.buildName,
        this.getSelectedPlatformName(),
        this.getSelectedProjectTypeName(),
        this.getSelectedRegionName(),
        this.getSelectedTestingType()
      )
      .subscribe(
        (response: any) => {
          const rows = this.extractRows(response)
            .filter(item => !this.isSchedulerRecord(item))
            .map(item => this.mapToRow(item))
            .filter(row => this.matchesFilters(row));

          this.allReportRows = rows;
          this.userOptions = this.extractUserOptions(rows);
          this.applyUserFilter();
          this.showAll = false;
          this.loading = false;
        },
        () => {
          this.allReportRows = [];
          this.reportRows = [];
          this.userOptions = [];
          this.selectedUsers = [];
          this.showUserDropdown = false;
          this.showAll = false;
          this.loading = false;
          this.errorMessage =
            'Unable to fetch execution report. Please try again.';
        }
      );
  }

  downloadReport() {
    if (!this.isFilterValid()) {
      return;
    }

    this.downloadLoading = true;
    this.errorMessage = '';

    this.dataservice
      .downloadExecutionReport(
        this.getSelectedBrandName(),
        this.fromDate,
        this.toDate,
        this.buildName,
        this.getSelectedPlatformName(),
        this.getSelectedProjectTypeName(),
        this.getSelectedRegionName(),
        this.getSelectedTestingType()
      )
      .subscribe(
        response => {
          const fileName = this.getFileNameFromResponse(response);
          const contentType =
            response.headers.get('content-type') || 'application/vnd.ms-excel';
          const body = response.body;

          if (!(body instanceof Blob)) {
            this.downloadLoading = false;
            this.errorMessage =
              'Download failed: server did not return a valid file.';
            return;
          }

          const blob =
            body.type && body.type !== ''
              ? body
              : new Blob([body], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => window.URL.revokeObjectURL(fileURL), 1000);
          this.downloadLoading = false;
        },
        () => {
          this.downloadLoading = false;
          this.errorMessage =
            'Unable to download execution report. Please try again.';
        }
      );
  }

  private loadProjectTypes() {
    this.dataservice.getAllProjectType().subscribe(
      (response: any) => {
        this.projectTypeList = Array.isArray(response) ? response : [];
        if (this.projectTypeList.length > 0) {
          this.selectedProjectTypeId = this.toNumber(
            this.projectTypeList[0].projectTypeId
          );
          this.loadRegionsByProjectType();
          this.loadPlatformsByProjectType();
        }
      },
      () => {
        this.projectTypeList = [];
      }
    );
  }

  private loadRegionsByProjectType() {
    if (!this.selectedProjectTypeId) {
      this.regionList = [];
      return;
    }

    this.dataservice
      .getAllRegionByProjectType(this.selectedProjectTypeId)
      .subscribe(
        (response: any) => {
          this.regionList = Array.isArray(response) ? response : [];
          if (this.regionList.length > 0) {
            this.selectedRegionId = this.toNumber(this.regionList[0].regionId);
            this.loadBrandsByRegion();
          }
        },
        () => {
          this.regionList = [];
        }
      );
  }

  private loadBrandsByRegion() {
    if (!this.selectedRegionId) {
      this.dataservice.getAllBrand().subscribe(
        (response: any) => {
          this.brandList = Array.isArray(response) ? response : [];
          this.selectedBrandId = 0;
        },
        () => {
          this.brandList = [];
        }
      );
      return;
    }

    this.dataservice.getBrandsByRegionId(this.selectedRegionId).subscribe(
      (response: any) => {
        this.brandList = Array.isArray(response) ? response : [];
        this.selectedBrandId = 0;
      },
      () => {
        this.brandList = [];
      }
    );
  }

  private loadPlatformsByProjectType() {
    if (!this.selectedProjectTypeId) {
      this.platformList = [];
      return;
    }

    this.dataservice
      .getPlatformByProjectTypeId(this.selectedProjectTypeId, true)
      .subscribe(
        (response: any) => {
          this.platformList = Array.isArray(response) ? response : [];
          this.selectedPlatformId = 0;
        },
        () => {
          this.platformList = [];
        }
      );
  }

  private extractRows(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response.listOfRecords)) {
      return response.listOfRecords;
    }

    if (Array.isArray(response.records)) {
      return response.records;
    }

    return [];
  }

  private mapToRow(item: any): AllExecutionReportRow {
    return {
      executionDate: this.getExecutionDate(item),
      projectType: this.pickValue(item, [
        'projectTypeName',
        'projectType',
        'projectName'
      ]),
      brand: this.pickValue(item, ['brandName', 'brand']),
      region: this.pickValue(item, ['regionName', 'region', 'regionCode']),
      platform: this.pickValue(item, ['platformName', 'platform']),
      buildName: this.pickValue(item, ['appVersion', 'buildName']),
      executionName: this.pickValue(item, [
        'jobName',
        'executionName',
        'testExecutionName'
      ]),
      testingType: this.pickValue(item, [
        'testingType',
        'testLevel',
        'executionType'
      ]),
      passCount: this.toNumber(item.passCount),
      failCount: this.toNumber(item.failCount),
      skipCount: this.toNumber(item.skipCount),
      jobStatus: this.toNumber(item.jobStatus),
      statusText: this.getStatusText(item),
      createdBy: this.pickValue(item, ['createdBy'])
    };
  }

  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (!target.closest('.user-filter-dropdown')) {
      this.showUserDropdown = false;
    }
  }

  onUserSelectionChange(user: string, checked: boolean) {
    if (!user) {
      return;
    }

    if (checked) {
      if (!this.selectedUsers.includes(user)) {
        this.selectedUsers = [...this.selectedUsers, user];
      }
    } else {
      this.selectedUsers = this.selectedUsers.filter(name => name !== user);
    }

    this.applyUserFilter();
  }

  selectAllUsers() {
    this.selectedUsers = [...this.userOptions];
    this.applyUserFilter();
  }

  clearUserSelection() {
    this.selectedUsers = [];
    this.applyUserFilter();
  }

  isUserSelected(user: string): boolean {
    return this.selectedUsers.includes(user);
  }

  areAllUsersSelected(): boolean {
    return (
      this.userOptions.length > 0 &&
      this.selectedUsers.length === this.userOptions.length
    );
  }

  get userFilterLabel(): string {
    if (!this.userOptions.length || !this.selectedUsers.length) {
      return 'All Users';
    }

    if (this.selectedUsers.length === this.userOptions.length) {
      return 'All Users';
    }

    if (this.selectedUsers.length === 1) {
      return this.selectedUsers[0];
    }

    return this.selectedUsers.length + ' Users Selected';
  }

  private applyUserFilter() {
    if (
      !this.selectedUsers.length ||
      this.selectedUsers.length === this.userOptions.length
    ) {
      this.reportRows = [...this.allReportRows];
      return;
    }

    const selected = new Set(this.selectedUsers);
    this.reportRows = this.allReportRows.filter(row =>
      selected.has(row.createdBy)
    );
  }

  private extractUserOptions(rows: AllExecutionReportRow[]): string[] {
    const users = rows
      .map(row => (row.createdBy || '').trim())
      .filter(user => user !== '');

    return Array.from(new Set(users)).sort((a, b) => a.localeCompare(b));
  }

  private getExecutionDate(item: any): string {
    if (item.executionDate) {
      return item.executionDate;
    }

    if (item.startDate && item.startTime) {
      return item.startDate + ' ' + item.startTime;
    }

    if (item.startDate) {
      return item.startDate;
    }

    return this.pickValue(item, ['createdOn', 'createdDate']);
  }

  private pickValue(item: any, keys: string[]): string {
    for (const key of keys) {
      if (
        item[key] !== undefined &&
        item[key] !== null &&
        String(item[key]).trim() !== ''
      ) {
        return String(item[key]);
      }
    }

    return 'NA';
  }

  private isSchedulerRecord(item: any): boolean {
    const scheduleFieldsPresent =
      !!item.schedulerName ||
      !!item.scheduleName ||
      !!item.schedulerKey ||
      !!item.scheduleId ||
      !!item.schedulerId ||
      !!item.scheduleFlag ||
      !!item.isScheduler;

    if (scheduleFieldsPresent) {
      return true;
    }

    const executionMode = (item.executionMode || '').toString().toLowerCase();
    const jobName = (
      item.jobName ||
      item.executionName ||
      item.testExecutionName ||
      ''
    )
      .toString()
      .toLowerCase();

    return executionMode.includes('scheduler') || jobName.includes('scheduler');
  }

  private matchesFilters(row: AllExecutionReportRow): boolean {
    const selectedProjectTypeName = this.getSelectedProjectTypeName();
    if (
      selectedProjectTypeName &&
      !this.caseInsensitiveEquals(row.projectType, selectedProjectTypeName)
    ) {
      return false;
    }

    const selectedRegionName = this.getSelectedRegionName();
    if (
      selectedRegionName &&
      !this.caseInsensitiveEquals(row.region, selectedRegionName)
    ) {
      return false;
    }

    const selectedBrandName = this.getSelectedBrandName();
    if (
      selectedBrandName &&
      !this.caseInsensitiveEquals(row.brand, selectedBrandName)
    ) {
      return false;
    }

    const selectedPlatformName = this.getSelectedPlatformName();
    if (
      selectedPlatformName &&
      !this.caseInsensitiveEquals(row.platform, selectedPlatformName)
    ) {
      return false;
    }

    const selectedTestingType = this.getSelectedTestingType();
    if (
      selectedTestingType &&
      !this.caseInsensitiveEquals(row.testingType, selectedTestingType)
    ) {
      return false;
    }

    if (this.buildName) {
      const matcher = this.buildName.trim().toLowerCase();
      const isMatch = (row.buildName || '').toLowerCase().includes(matcher);
      if (!isMatch) {
        return false;
      }
    }

    const rowDate = moment(row.executionDate);
    if (rowDate.isValid()) {
      const from = moment(this.fromDate, 'YYYY-MM-DD').startOf('day');
      const to = moment(this.toDate, 'YYYY-MM-DD').endOf('day');
      if (rowDate.isBefore(from) || rowDate.isAfter(to)) {
        return false;
      }
    }

    return true;
  }

  private caseInsensitiveEquals(value1: string, value2: string): boolean {
    return (value1 || '').toLowerCase() === (value2 || '').toLowerCase();
  }

  private isFilterValid(): boolean {
    if (!this.fromDate || !this.toDate) {
      this.errorMessage = 'From date and To date are required.';
      return false;
    }

    this.errorMessage = '';
    return true;
  }

  private getSelectedProjectTypeName(): string {
    const selected = this.projectTypeList.find(
      item => this.toNumber(item.projectTypeId) === this.selectedProjectTypeId
    );
    return selected && selected.projectTypeName ? selected.projectTypeName : '';
  }

  private getSelectedRegionName(): string {
    if (!this.selectedRegionId) {
      return '';
    }

    const selected = this.regionList.find(
      item => this.toNumber(item.regionId) === this.selectedRegionId
    );
    if (!selected) {
      return '';
    }

    return selected.regionName || selected.regionCode || '';
  }

  private getSelectedBrandName(): string {
    if (!this.selectedBrandId) {
      return '';
    }

    const selected = this.brandList.find(
      item => this.toNumber(item.brandId) === this.selectedBrandId
    );
    return selected && selected.brandName ? selected.brandName : '';
  }

  private getSelectedPlatformName(): string {
    if (!this.selectedPlatformId) {
      return '';
    }

    const selected = this.platformList.find(
      item => this.toNumber(item.platformId) === this.selectedPlatformId
    );
    return selected && selected.platformName ? selected.platformName : '';
  }

  private getSelectedTestingType(): string {
    return this.selectedTestingType === 'All' ? '' : this.selectedTestingType;
  }

  getStatusClass(row: AllExecutionReportRow): string {
    if (row.jobStatus === 1) {
      return 'runningBtn';
    }
    if (row.jobStatus === 2) {
      return 'passedBtn';
    }
    if (row.jobStatus === 3) {
      return 'failedBtn';
    }
    if (row.jobStatus === 4) {
      return 'incompleteBtn';
    }

    return 'unknownBtn';
  }

  private getStatusText(item: any): string {
    if (item.statusText) {
      return item.statusText;
    }

    const status = this.toNumber(item.jobStatus);
    if (status === 1) {
      return 'Running';
    }
    if (status === 2) {
      return 'Completed';
    }
    if (status === 3) {
      return 'Incomplete';
    }
    if (status === 4) {
      return 'Terminated';
    }

    return 'Unknown';
  }

  private getStatusCount(status: number): number {
    return this.reportRows.filter(row => row.jobStatus === status).length;
  }

  private toNumber(value: any): number {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  private getFileNameFromResponse(response: any): string {
    const contentDisposition =
      response.headers.get('content-disposition') || '';
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const match = /filename=\"?([^\";]+)\"?/i.exec(contentDisposition);
    if (match && match[1]) {
      return match[1].trim();
    }

    return 'execution_report.xls';
  }
}
